import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import classNames from 'classnames';
import VimeoPlayer from '@vimeo/player';

import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import Spinner from '../../../global/Spinner';
import ArrowLeft from '../../../icons/ArrowLeft';
import DismissButton from '../../../global/DismissButton';
import ImagePreload from '../../../global/ImagePreload';
import { getThumbBySize } from '../../../../util/photo';
import Button from '../../../global/Button';
import { getAttributionName } from '../../../../util/account';
import PauseButtonIcon from '../../../icons/PauseButtonIcon';
import PlayButtonIcon from '../../../icons/PlayButtonIcon';
import ContributorProfileBadge from '../../../global/ContributorProfileBadge';
import { VIDEO_PROVIDER } from '../../../../constants/videos';

export const LOAD_MORE_THRESHOLD = 5;

const propTypes = {
  recordId: PropTypes.string.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  loadMore: PropTypes.func,
  shouldLoadMore: PropTypes.bool,
  recordVideoAccessor: PropTypes.string,
  renderVideo: PropTypes.func,
  actions: PropTypes.func,
  footer: PropTypes.func,
  imageSizeCode: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  gridPath: PropTypes.string,
};

const VideoGridViewer = ({
  records,
  recordId,
  shouldLoadMore,
  loadMore,
  recordVideoAccessor,
  renderVideo,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  actions,
  footer,
  gridPath,
}) => {
  const [loading, setLoading] = useState(false);
  const [detailsHidden, setDetailsHidden] = useState(true);
  const match = useRouteMatch();
  const history = useHistory();
  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');
  const videoElementRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [dragTime, setDragTime] = useState(null);
  const inactivityTimeoutRef = useRef(null);

  const setupVimeoPlayer = () => {
    vimeoPlayerRef.current = new VimeoPlayer(videoElementRef.current);
    vimeoPlayerRef.current.on('play', () => {
      setIsVideoPlaying(true);
    });
    vimeoPlayerRef.current.on('pause', () => {
      setIsVideoPlaying(false);
    });
    vimeoPlayerRef.current.on('timeupdate', data => {
      if (!isSeeking) {
        setCurrentTime(data.seconds);
        setDuration(data.duration);
      }
    });
    vimeoPlayerRef.current.getDuration().then(dur => {
      setDuration(dur);
    });
  };

  const resetInactivityTimer = () => {
    setShowControls(true);

    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    inactivityTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const handleMouseMove = () => {
    resetInactivityTimer();
  };

  const handleMouseLeave = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    setShowControls(false);
  };

  useEffect(() => {
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);

  const formatTime = seconds => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateSeekTime = (e, element) => {
    const rect = element.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return pos * duration;
  };

  const handleSeek = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!vimeoPlayerRef.current || !duration) return;

    const progressBar = e.currentTarget;
    const seekTime = calculateSeekTime(e, progressBar);

    setIsSeeking(true);
    setDragTime(null);
    vimeoPlayerRef.current
      .setCurrentTime(seekTime)
      .then(() => {
        setCurrentTime(seekTime);
        setIsSeeking(false);
      })
      .catch(() => {
        setIsSeeking(false);
      });
  };

  const handleProgressMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!duration || !vimeoPlayerRef.current) return;

    const progressBar = e.currentTarget;
    setIsSeeking(true);

    const handleProgressMouseMove = moveEvent => {
      moveEvent.preventDefault();
      const seekTime = calculateSeekTime(moveEvent, progressBar);
      setDragTime(seekTime);

      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.setCurrentTime(seekTime).catch(() => {});
      }
    };

    const handleMouseUp = upEvent => {
      upEvent.preventDefault();
      const seekTime = calculateSeekTime(upEvent, progressBar);

      setDragTime(null);

      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current
          .setCurrentTime(seekTime)
          .then(() => {
            setCurrentTime(seekTime);
            setIsSeeking(false);
          })
          .catch(() => {
            setIsSeeking(false);
          });
      }

      document.removeEventListener('mousemove', handleProgressMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    const initialSeekTime = calculateSeekTime(e, progressBar);
    setDragTime(initialSeekTime);

    if (vimeoPlayerRef.current) {
      vimeoPlayerRef.current.setCurrentTime(initialSeekTime).catch(() => {});
    }

    document.addEventListener('mousemove', handleProgressMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Checks if more records are necessary to load and loads them
   * if we're approaching the end of the navigation (right-hand-wise)
   * so we would need to load more records to continue to the next record.
   *
   * @returns {Promise<void>}
   */
  const maybeLoadMoreRecords = async () => {
    if (loading || !loadMore || !shouldLoadMore) {
      return;
    }

    const currentRecordIndex = records.findIndex(({ id }) => recordId === id);

    // If we're approaching the end and there are more records, load them
    // to keep a steady progression to the next record
    if (
      records.length - currentRecordIndex <= LOAD_MORE_THRESHOLD ||
      // If we land on a photo not loaded yet, we need to query stuff until we get it
      currentRecordIndex === -1
    ) {
      setLoading(true);
      await loadMore();
      setLoading(false);
    }
  };

  useEffect(() => {
    maybeLoadMoreRecords();
  }, [recordId, records.length, loading]);

  /**
   * Gets the adjacent record ID (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {string|undefined}
   */
  const getAdjacentRecordId = adjacency => {
    const currentRecordIndex = records.findIndex(({ id }) => id === recordId);
    const nextRecord = records[currentRecordIndex + 1];
    const prevRecord = records[currentRecordIndex - 1];

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord) {
      return nextRecord.id;
    }

    if (adjacency === -1 && prevRecord) {
      return prevRecord.id;
    }

    return undefined;
  };

  /**
   * Navigates through records (ie previous or next).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   */
  const onNavigateRecords = adjacency => {
    const nextRecordId = getAdjacentRecordId(adjacency);

    if (!nextRecordId) {
      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  /**
   * Dismisses the Photo Viewer
   */
  const onDismiss = () => {
    history.replace(gridPath);
  };

  /**
   * Helper method that navigates to either the next or prev record
   * depending on whether they exist (with that priority) or dismisses
   * the video viewer in case neither exist.
   */
  const onNavigateNextAfterRemoval = () => {
    const nextRecordId = getAdjacentRecordId(1);

    if (!nextRecordId) {
      const prevRecordId = getAdjacentRecordId(-1);

      if (!prevRecordId) {
        onDismiss();
      } else {
        history.replace(`${basePath}/${prevRecordId}`);
      }

      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  const getVideoFromRecord = record => {
    if (!record) {
      return undefined;
    }

    return recordVideoAccessor ? record?.[recordVideoAccessor] : record;
  };

  const record = records.find(({ id }) => recordId === id);
  const nextRecordId = getAdjacentRecordId(1);
  const prevRecordId = getAdjacentRecordId(-1);

  const video = getVideoFromRecord(record);
  const nextVideo = getVideoFromRecord(
    records.find(({ id }) => nextRecordId === id),
  );

  const isVimeo =
    !video?.video_provider || video?.video_provider === VIDEO_PROVIDER.VIMEO;

  const detailsClasses = classNames({
    'video-details': true,
    'video-details-generic': !isVimeo,
    hidden: detailsHidden,
  });

  const iframeClasses = classNames({
    'video-viewer-iframe': true,
    'video-viewer-iframe-generic': !isVimeo,
  });

  const uploaderAccount = video?.contributor_account ?? video?.account;

  useEffect(() => {
    if (videoElementRef?.current && isVimeo) {
      // Create a vimeoPlayerRef:
      setupVimeoPlayer();
    }
  }, [video]);

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName="video-grid-viewer-overlay"
      className="video-grid-viewer"
    >
      <div className="video-grid-viewer-header">
        <div className="video-grid-viewer-header-left">
          <DismissButton
            className="video-grid-viewer-btn-dismiss"
            onClick={onDismiss}
          />
        </div>

        {!!record && actions && (
          <div className="video-grid-viewer-header-right">
            {actions({
              record,
              onPrevRecord: () => onNavigateRecords(-1),
              onNextRecord: () => onNavigateRecords(1),
              onNavigateNextAfterRemoval,
              nextRecordId,
              prevRecordId,
            })}
          </div>
        )}
      </div>

      <button
        className="video-grid-viewer-nav video-grid-viewer-nav-prev"
        onClick={() => onNavigateRecords(-1)}
        disabled={!prevRecordId}
      >
        <span className="video-grid-viewer-nav-outline">
          <ArrowLeft color="#fff" />
        </span>
      </button>
      <div className="video-grid-viewer-body">
        <div className="video-grid-viewer-content">
          {video ? (
            (() => {
              if (renderVideo) {
                return renderVideo({ video, record });
              }

              if (!isVimeo) {
                return (
                  <div className="video-viewer-main">
                    <div className="video-viewer-iframe-wrapper">
                      <iframe
                        className={iframeClasses}
                        title={video.name}
                        src={video.embed_url}
                        allowFullScreen
                        webkitallowfullscreen="true"
                        mozallowfullscreen="true"
                        allow="autoplay; encrypted-media"
                      />
                    </div>
                    <div className={detailsClasses}>
                      {uploaderAccount && (
                        <ContributorProfileBadge
                          displayName={getAttributionName(uploaderAccount)}
                          displayPhotoSrc={
                            getThumbBySize(
                              uploaderAccount?.avatar_photo?.thumbnail_urls,
                              IMAGE_SIZE_CODE.SMALL,
                            )?.url ?? undefined
                          }
                          prependText="By"
                          className="light mb-1"
                          accountId={video?.attribution?.account_id}
                          photoAttributionPageEnabled={
                            video?.attribution?.photo_attribution_page
                              ?.is_enabled
                          }
                        />
                      )}
                      <div className="d-flex mb-1">
                        <h3 className="mb-0 mr-1">{video.name}</h3>
                        <Button
                          btnStyle="reset"
                          onClick={() => setDetailsHidden(!detailsHidden)}
                          className="text-bold text-white ml-auto align-items-center"
                        >
                          {detailsHidden ? 'Show Details' : 'Hide'}
                        </Button>
                      </div>
                      <p>{video.description}</p>
                    </div>

                    <div className="photo-grid-viewer-footer">
                      {!!records && footer && (
                        <>
                          {footer({
                            record,
                            onPrevRecord: () => onNavigateRecords(-1),
                            onNextRecord: () => onNavigateRecords(1),
                            onNavigateNextAfterRemoval,
                            nextRecordId,
                            prevRecordId,
                          })}
                        </>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  className="video-viewer-main"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="video-viewer-iframe-wrapper">
                    <iframe
                      className="video-viewer-iframe"
                      title={video.name}
                      src={`${video.embed_url}&controls=false`}
                      allowFullScreen
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                      allow="autoplay; encrypted-media"
                      ref={videoElementRef}
                    />

                    <Button
                      btnStyle="reset"
                      btnSize="sm"
                      className={classNames('video-viewer-play-button', {
                        'video-viewer-controls-hidden': !showControls,
                      })}
                      onClick={() => {
                        if (!vimeoPlayerRef.current) {
                          setupVimeoPlayer();
                        }

                        if (isVideoPlaying) {
                          vimeoPlayerRef?.current?.pause();
                        } else {
                          vimeoPlayerRef?.current?.play();
                        }
                      }}
                    >
                      {isVideoPlaying ? (
                        <div className="video-viewer-play-button-icon-wrapper video-viewer-play-button-playing">
                          <PauseButtonIcon size={40} color="#fff" />
                        </div>
                      ) : (
                        <div className="video-viewer-play-button-icon-wrapper">
                          <PlayButtonIcon size={40} color="#fff" />
                        </div>
                      )}
                    </Button>

                    <div
                      className={classNames('video-viewer-controls', {
                        'video-viewer-controls-playing': isVideoPlaying,
                        'video-viewer-controls-hidden': !showControls,
                      })}
                    >
                      <div className="video-viewer-time-display">
                        <span className="video-viewer-current-time">
                          {formatTime(
                            dragTime !== null ? dragTime : currentTime,
                          )}
                        </span>
                        <span className="video-viewer-time-separator">/</span>
                        <span className="video-viewer-duration">
                          {formatTime(duration)}
                        </span>
                      </div>
                      {}
                      <div
                        className="video-viewer-progress-bar"
                        onClick={handleSeek}
                        onMouseDown={handleProgressMouseDown}
                      >
                        <div className="video-viewer-progress-bar-background">
                          <div
                            className="video-viewer-progress-bar-fill"
                            style={{
                              width: `${
                                duration > 0
                                  ? ((dragTime !== null
                                      ? dragTime
                                      : currentTime) /
                                      duration) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={detailsClasses}>
                    {uploaderAccount && (
                      <ContributorProfileBadge
                        displayName={getAttributionName(uploaderAccount)}
                        displayPhotoSrc={
                          getThumbBySize(
                            uploaderAccount?.avatar_photo?.thumbnail_urls,
                            IMAGE_SIZE_CODE.SMALL,
                          )?.url ?? undefined
                        }
                        prependText="By"
                        className="light mb-1"
                        accountId={video?.attribution?.account_id}
                        photoAttributionPageEnabled={
                          video?.attribution?.photo_attribution_page?.is_enabled
                        }
                      />
                    )}
                    <div className="d-flex mb-1">
                      <h3 className="mb-0 mr-1">{video.name}</h3>
                      <Button
                        btnStyle="reset"
                        onClick={() => setDetailsHidden(!detailsHidden)}
                        className="text-bold text-white ml-auto align-items-center"
                      >
                        {detailsHidden ? 'Show Details' : 'Hide'}
                      </Button>
                    </div>
                    <p>{video.description}</p>
                  </div>

                  <div className="photo-grid-viewer-footer">
                    {!!records && footer && (
                      <>
                        {footer({
                          record,
                          onPrevRecord: () => onNavigateRecords(-1),
                          onNextRecord: () => onNavigateRecords(1),
                          onNavigateNextAfterRemoval,
                          nextRecordId,
                          prevRecordId,
                        })}
                      </>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <Spinner />
          )}
        </div>
      </div>
      <button
        className="video-grid-viewer-nav video-grid-viewer-nav-next"
        onClick={() => onNavigateRecords(1)}
        disabled={!nextRecordId}
      >
        <span className="video-grid-viewer-nav-outline">
          <ArrowLeft color="#fff" />
        </span>
      </button>

      {nextVideo && (
        <ImagePreload
          urls={[getThumbBySize(nextVideo.thumbnail_urls, imageSizeCode)?.url]}
        />
      )}

      <HotKeys
        handlers={{
          esc: onDismiss,
          escape: onDismiss,
          right: nextRecordId ? () => onNavigateRecords(1) : undefined,
          left: prevRecordId ? () => onNavigateRecords(-1) : undefined,
        }}
        attach={window}
        focused
      />
    </Modal>
  );
};

VideoGridViewer.propTypes = propTypes;

export default VideoGridViewer;
